import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
  useMemo,
} from "react";
import { onShowAlerts } from "../presentation/component/popup";
import {
  getAuthToken,
  removeAuthToken,
  saveAuthToken,
} from "../core/persistence/LocalStorage";
import { useQuery } from "react-query";
import { useSnackbar } from "./useSnackbar";
import MainUseCase from "../usecases/MainUseCase";
import MainApi from "../api/MainApi";
import { TokenResult } from "../domain/entity/Main";

type AuthContextValue = {
  setToken: (auth: string | null) => void;
  token?: string | null;
  setExpired: (isExpired: boolean) => void;
  isAuthenticated: boolean;
  setIsAuthenticated: (isAuth: boolean) => void;
};

export const AuthContext = createContext<AuthContextValue>({
  setToken: () => {},
  token: null,
  setExpired: () => {},
  isAuthenticated: true,
  setIsAuthenticated: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>();
  const [isAuthenticated, setIsAuthenticatedState] = useState<boolean>(true);

  useEffect(() => {
    if (token) {
      saveAuthToken(token);
    } else {
      removeAuthToken();
    }
  }, [token]);

  function setToken(auth: string | null) {
    setTokenState(auth);
  }

  function setIsAuthenticated(isAuth: boolean) {
    setIsAuthenticatedState(isAuth);
  }

  function setExpired(isExpired: boolean) {
    if (isExpired) {
      onShowAlerts("Token Expired", setToken);
    }
  }

  return (
    <AuthContext.Provider
      value={{
        setToken,
        token,
        setExpired,
        isAuthenticated: !!token && isAuthenticated,
        setIsAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const { setToken, token, setExpired, isAuthenticated, setIsAuthenticated } =
    useContext(AuthContext);

  const windowUrl = window.location.search;
  const params = useMemo(() => new URLSearchParams(windowUrl), [windowUrl]);
  const invoiceId = useMemo(() => {
    return params.get("invoiceId");
  }, [params]);

  useEffect(() => {
    if (invoiceId) {
      localStorage.setItem("invoiceId", invoiceId);
    }
  }, [invoiceId]);

  const repository = new MainUseCase(new MainApi());
  const { showSnackbar } = useSnackbar();

  const { isLoading: isTokenLoading } = useQuery({
    enabled: !token && invoiceId !== null,
    refetchOnWindowFocus: false,
    retry: false,
    queryKey: ["token", invoiceId],
    queryFn: () => repository.getToken({ grantType: "client_credentials" }),
    onSuccess: (results: TokenResult) => {
      // console.log("results TOKEN", results);
      if (results.responseCode === "20000") {
        setToken(results.accessToken);
        setIsAuthenticated(true);
      }
    },
    onError: async (error: any) => {
      if (
        error.response === undefined ||
        error.response.data === "Read timed out" ||
        error.response.status === 500
      ) {
        setIsAuthenticated(false);
        showSnackbar("Error - Terjadi Kendala");
      } else if (
        error.response.data !== undefined &&
        error.response.status !== 401
      ) {
        setIsAuthenticated(false);
        showSnackbar(error.response.data.responseMessage);
      } else if (error.response.status === 401) {
        setExpired(true);
      } else {
        setIsAuthenticated(false);
        showSnackbar("Error - Terjadi Kendala");
      }
    },
  });

  return { setToken, token, setExpired, isAuthenticated, isTokenLoading };
}
